import React, { useState, useEffect, useCallback } from 'react';
import './Toast.css';

// Toast notification component
const Toast = ({ id, type, title, message, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(() => {
      if (typeof onClose === 'function') {
        onClose(id);
      }
    }, 300);
  }, [id, onClose, isExiting]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [handleClose]);

  const icons = {
    error: '✕',
    success: '✓',
    warning: '⚠',
    info: 'ℹ'
  };

  return (
    <div className={`InvoiceForm__toast InvoiceForm__toast--${type} ${isExiting ? 'InvoiceForm__toast--exiting' : ''}`}>
      <div className="InvoiceForm__toast-icon">
        {icons[type]}
      </div>
      <div className="InvoiceForm__toast-content">
        <div className="InvoiceForm__toast-title">{title}</div>
        {message && <div className="InvoiceForm__toast-message">{message}</div>}
      </div>
      <button 
        className="InvoiceForm__toast-close"
        onClick={handleClose}
        aria-label="Close notification"
      >
        ×
      </button>
      <div className="InvoiceForm__toast-progress"></div>
    </div>
  );
};

// Toast container and hook
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, title, message = '') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showError = useCallback((title, message) => {
    return addToast('error', title, message);
  }, [addToast]);

  const showSuccess = useCallback((title, message) => {
    return addToast('success', title, message);
  }, [addToast]);

  const showWarning = useCallback((title, message) => {
    return addToast('warning', title, message);
  }, [addToast]);

  const showInfo = useCallback((title, message) => {
    return addToast('info', title, message);
  }, [addToast]);

  // Return ToastContainer as a component that uses current toasts
  const ToastContainer = useCallback(() => {
    if (toasts.length === 0) return null;

    return (
      <div className="InvoiceForm__toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={removeToast}
          />
        ))}
      </div>
    );
  }, [toasts, removeToast]);

  return {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    ToastContainer
  };
};

export default Toast;